/**
 * magnet-templates.js — starter-flow gallery for "New magnet from a template".
 *
 * A template uses the SAME bare-key shape
 * collectAppearance()/applyAppearance() already use (magnets-route.js /
 * magnets.js), plus steps and optional named routes in the SDK's real shapes
 * (step_type/step_columns/step_prompts/step_options/step_description — see
 * starterFlow() in service-api's magnets.js). No new backend surface: a
 * template is applied by PATCHing a freshly-provisioned DRAFT magnet with
 * this data, the exact same PATCH /:guid the Save button already sends —
 * see provisionAndOpen()'s optional `template` param in magnets-route.js.
 *
 * Answers use shared LEAD_CAPTURE_FIELDS or stable step_answer metadata and its
 * additional-answer key. Navigation-only steps have no saved field.
 *
 * `category`/`tags`/`featured` are browse-page metadata only: the /templates
 * gallery filters and badges on them. They are never sent to the API (see
 * applyTemplate(), which only reads name/appearance/steps/routes), so adding one to a
 * template can never change the magnet it produces.
 */

import { HANDOFF, setHandoff, takeHandoff, peekHandoff } from '../core/routeHandoff.js';
import api from '../core/corerest_api.js';
import { guardedFetch } from '../core/navGeneration.js';
import { templateAppearance, MAGNET_TEMPLATE_DEFAULT_APPEARANCE, ASSISTANT_WELCOME_TEMPLATE, HELP_CONTACT_TEMPLATE, SERVICE_STARTER_TEMPLATES, LEAD_CAPTURE_FIELDS } from '@sse/shared';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

/** Filter-chip labels for the browse page. Keys are the only valid `category` values. */
export const TEMPLATE_CATEGORIES = Object.freeze({
  lead_capture: 'Lead capture',
  booking: 'Booking',
  engagement: 'Engagement',
  support: 'Support',
});

export const MAGNET_TEMPLATES = [
  {
    id: 'lead_capture',
    name: 'Lead Capture',
    description: 'A general-purpose qualify-and-capture flow. Ask what a visitor needs, then get their contact info.',
    icon: 'person_add',
    category: 'lead_capture',
    tags: ['Contact info', 'Qualifying'],
    featured: true,
    appearance: {
      title: 'How can we help?',
      subtitle: 'A few details so we can follow up.',
      home: false,
      summary: true,
      summary_heading: 'Review your inquiry',
      summary_confirm_label: 'Send inquiry',
      success_title: 'Inquiry sent',
      success_text: 'We have your details. Our team will follow up using the contact information you shared.',
    },
    steps: [
      { id: '1', step_type: 'single_select', step_columns: ['product_interest'], step_prompts: ['What would you like our team to help with?'], step_options: [{ label: 'Request a quote', goto: '' }, { label: 'Ask about a service', goto: '' }, { label: 'Discuss a project', goto: '' }], step_description: '' },
      { id: '2', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'email', step_columns: ['email'], step_prompts: ['Which email should we use to follow up?'], step_options: [], step_description: '' },
      { id: '4', step_type: 'phone', step_columns: ['phone'], step_prompts: ['Would you also like to share a phone number? This is optional.'], step_options: [], step_description: '', step_config: { optional: true, skip_label: 'Use email only' } },
    ],
  },
  {
    id: 'appointment_booking',
    name: 'Appointment Booking',
    description: 'Collect contact details and a preferred appointment time for your team to confirm. Saves the time in Notes / description.',
    icon: 'event_available',
    category: 'booking',
    tags: ['Scheduling', 'Appointment request'],
    featured: false,
    appearance: {
      title: 'Request an appointment',
      subtitle: 'Choose a preferred time. We’ll confirm availability.',
      home: false,
      summary: true,
      summary_heading: 'Review your appointment request',
      summary_confirm_label: 'Request appointment',
      success_title: 'Appointment request sent',
      success_text: 'We received your preferred time. Our team will email you to confirm availability; your appointment is not booked yet.',
    },
    steps: [
      { id: '1', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '2', step_type: 'email', step_columns: ['email'], step_prompts: ['Which email should we use to confirm a time?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'phone', step_columns: ['phone'], step_prompts: ['A phone number is optional if you’d also like us to call.'], step_options: [], step_description: '', step_config: { optional: true, skip_label: 'Use email only' } },
      { id: '4', step_type: 'scheduler', step_columns: ['description'], step_prompts: ['Which time would you prefer? We’ll confirm availability by email.'], step_options: [], step_description: '', step_config: { meeting_label: 'Preferred appointment time', start: '09:00', end: '17:00', mins: 30, days: 14 } },
    ],
  },
  {
    id: 'support_triage',
    name: 'Support Triage',
    description: 'Route a visitor to the right kind of help before they describe the issue, so your team sees the full picture up front.',
    icon: 'support_agent',
    category: 'support',
    tags: ['Routing', 'Existing customers'],
    featured: false,
    appearance: {
      title: 'How can we help?',
      subtitle: 'Send a request for our team to review.',
      home: false,
      summary: true,
      summary_heading: 'Review your support request',
      summary_confirm_label: 'Send support request',
      success_title: 'Support request sent',
      success_text: 'Our team has your request and will follow up by email.',
    },
    steps: [
      { id: '1', step_type: 'multi_select', step_columns: ['product_interest'], step_prompts: ['What do you need help with? Pick everything that applies.'], step_options: [{ label: 'Billing question', goto: '' }, { label: 'Technical issue', goto: '' }, { label: 'Feature request', goto: '' }, { label: 'Something else', goto: '' }], step_description: '' },
      { id: '2', step_type: 'text', step_columns: ['description'], step_prompts: ['Can you describe what’s going on?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '4', step_type: 'email', step_columns: ['email'], step_prompts: ['Best email to follow up on this?'], step_options: [], step_description: '' },
    ],
  },
  {
    id: 'quote_request',
    name: 'Quote Request',
    description: 'For local and home-service businesses. Describe the job, leave contact info, get a callback.',
    icon: 'request_quote',
    category: 'lead_capture',
    tags: ['Local services', 'Callback'],
    featured: false,
    appearance: {
      title: 'Request a quote',
      subtitle: 'Tell us about the job and we’ll follow up.',
      home: false,
      summary: true,
      summary_heading: 'Review your quote request',
      summary_confirm_label: 'Request quote',
      success_title: 'Quote request sent',
      success_text: 'We have the job details. Our team will contact you to discuss the work and next steps.',
    },
    steps: [
      { id: '1', step_type: 'text', step_columns: ['description'], step_prompts: ['For example: a roof repair, fence installation, or leak repair.', 'What work do you need?'], step_options: [], step_description: '' },
      { id: '2', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'phone', step_columns: ['phone'], step_prompts: ['Best phone number for a quick callback?'], step_options: [], step_description: '' },
      { id: '4', step_type: 'email', step_columns: ['email'], step_prompts: ['Would you also like to share an email? This is optional.'], step_options: [], step_description: '', step_config: { optional: true, skip_label: 'Use phone only' } },
    ],
  },
  {
    id: 'site_navigation_guide',
    name: 'Site Navigation Guide',
    description: 'A branching menu for content or reference sites. Visitors pick where they want to go instead of filling out a form. No contact info collected.',
    icon: 'signpost',
    category: 'engagement',
    tags: ['Branching', 'No contact info'],
    featured: false,
    appearance: {
      title: 'Looking for something?',
      subtitle: 'Choose where you’d like to go.',
      home: false,
      summary: false,
    },
    // Uses per-option `goto` + a terminal message step's step_config.terminal/cta —
    // both already fully supported at the data/runtime layer (see the SDK's
    // renderTerminal()); this was the first template to actually exercise `goto`
    // (exit_intent_offer is the second). Placeholder branches + example.com URLs
    // are deliberate — this reads as "customize me before publishing," not a real
    // destination, same as every other template's placeholder copy.
    steps: [
      { id: '1', step_type: 'single_select', step_columns: [], step_prompts: ['What would you like to find?'], step_options: [
        { label: 'Browse products', goto: '2' },
        { label: 'See pricing', goto: '3' },
        { label: 'Read our guide', goto: '4' },
        { label: 'Something else', goto: '5' },
      ], step_description: '' },
      { id: '2', step_type: 'message', step_columns: [], step_prompts: ['Explore our products.'], step_options: [], step_description: '', step_config: { terminal: true, cta: { label: 'Browse products', url: 'https://example.com/products' } } },
      { id: '3', step_type: 'message', step_columns: [], step_prompts: ['Find pricing details here.'], step_options: [], step_description: '', step_config: { terminal: true, cta: { label: 'See pricing', url: 'https://example.com/pricing' } } },
      { id: '4', step_type: 'message', step_columns: [], step_prompts: ['Read our guide at your own pace.'], step_options: [], step_description: '', step_config: { terminal: true, cta: { label: 'Read our guide', url: 'https://example.com/guide' } } },
      { id: '5', step_type: 'message', step_columns: [], step_prompts: ['Thanks for stopping by. Feel free to reach out if you need anything else.'], step_options: [], step_description: '', step_config: { terminal: true } },
    ],
  },
  {
    id: 'newsletter_signup',
    name: 'Newsletter Signup',
    description: 'Pick a topic, leave an email, done. The shortest path from visitor to subscriber.',
    icon: 'mail',
    category: 'lead_capture',
    tags: ['Email list', 'Two questions'],
    featured: false,
    appearance: {
      title: 'Join the list',
      subtitle: 'Choose the updates you’re interested in.',
      home: false,
      summary: true,
      summary_heading: 'Review your signup',
      summary_confirm_label: 'Join the list',
      success_title: 'Signup received',
      success_text: 'We have your email and update preferences. Thanks for your interest.',
    },
    steps: [
      { id: '1', step_type: 'single_select', step_columns: ['product_interest'], step_prompts: ['What would you like to hear about?'], step_options: [
        { label: 'Product updates', goto: '' },
        { label: 'Tips and how-tos', goto: '' },
        { label: 'Deals and offers', goto: '' },
      ], step_description: '' },
      { id: '2', step_type: 'email', step_columns: ['email'], step_prompts: ['Where should we send it?'], step_options: [], step_description: '' },
      // The success screen is shown only after capture succeeds. A message step here
      // would play before submission and could claim success while the request fails.
    ],
  },
  {
    id: 'demo_request',
    name: 'Demo Request',
    description: 'Collect a B2B inquiry and a preferred demo time for your team to confirm. Saves the time in Notes / description.',
    icon: 'co_present',
    category: 'booking',
    tags: ['B2B', 'Scheduling'],
    featured: false,
    appearance: {
      title: 'Request a demo',
      subtitle: 'Tell us about your team and choose a preferred time.',
      home: false,
      summary: true,
      summary_heading: 'Review your demo request',
      summary_confirm_label: 'Request demo',
      success_title: 'Demo request sent',
      success_text: 'We have your details and preferred time. Our team will email you to confirm availability; your demo is not booked yet.',
    },
    steps: [
      { id: '1', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '2', step_type: 'email', step_columns: ['email'], step_prompts: ['What’s your work email?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'text', step_columns: ['company'], step_prompts: ['Where do you work?'], step_options: [], step_description: '' },
      { id: '4', step_type: 'single_select', step_columns: ['industry'], step_prompts: ['What kind of business is it?'], step_options: [
        { label: 'Agency', goto: '' },
        { label: 'Ecommerce', goto: '' },
        { label: 'SaaS', goto: '' },
        { label: 'Other', goto: '' },
      ], step_description: '' },
      // Same step_config shape as appointment_booking's scheduler step.
      { id: '5', step_type: 'scheduler', step_columns: ['description'], step_prompts: ['Which time would you prefer for a demo? We’ll confirm availability by email.'], step_options: [], step_description: '', step_config: { meeting_label: 'Preferred demo time', start: '09:00', end: '17:00', mins: 30, days: 14 } },
    ],
  },
  {
    id: 'feedback_pulse',
    name: 'Feedback Pulse',
    description: 'A quick experience check with an optional reply address. Saves the response in Notes / description.',
    icon: 'reviews',
    category: 'engagement',
    tags: ['Feedback', 'Existing customers'],
    featured: false,
    appearance: {
      title: 'Share your feedback',
      subtitle: 'Tell us how it went. Contact details are optional.',
      home: false,
      summary: true,
      summary_heading: 'Review your feedback',
      summary_confirm_label: 'Send feedback',
      success_title: 'Feedback received',
      success_text: 'Thanks for sharing your experience. Your feedback has been sent to our team.',
    },
    steps: [
      // Feedback belongs in notes; `rating` is the lead's Hot/Warm/Cold sales rating.
      // Keep this pulse to one response so a later comment cannot overwrite it.
      { id: '1', step_type: 'single_select', step_columns: ['description'], step_prompts: ['How was your experience with us?'], step_options: [
        { label: 'Great', goto: '' },
        { label: 'Okay', goto: '' },
        { label: 'Not great', goto: '' },
      ], step_description: '' },
      { id: '3', step_type: 'email', step_columns: ['email'], step_prompts: ['Want a reply? You can leave an email or skip this step.'], step_options: [], step_description: '', step_config: { optional: true, skip_label: 'Send without an email' } },
    ],
  },
  {
    id: 'exit_intent_offer',
    name: 'Exit Intent Offer',
    description: 'Collect requests for your offer, with a polite exit for anyone who says no. Customize the offer and arrange delivery before publishing.',
    icon: 'door_open',
    category: 'engagement',
    tags: ['Offer', 'Branching'],
    featured: false,
    appearance: {
      title: 'Before you go',
      subtitle: 'Interested in our current offer?',
      home: false,
      summary: true,
      summary_heading: 'Review your offer request',
      summary_confirm_label: 'Request offer',
      success_title: 'Offer request received',
      success_text: 'We have your email and your interest in this offer. Our team will follow up with the details.',
    },
    // Second template to exercise `goto` (site_navigation_guide was the first):
    // "No thanks" jumps past the email ask straight to its own terminal, so a
    // visitor who declines is never asked for contact info. That decline step is
    // the ONLY terminal here — it collects nothing, so stopping without a capture
    // is correct. The accept path must NOT end terminal (the SDK never POSTs the
    // capture from a terminal step — see advance() in shared/machfivemagnet.js),
    // so the email step falls off the end of the flow into finish()/capture.
    // Array order matters for that fall-through: the decline
    // terminal is parked at index 1, ahead of the email step, so the accept tail
    // (email) occupies the end of the array. goto targets are id-based,
    // so the jumps are unaffected by the ordering.
    steps: [
      { id: '1', step_type: 'single_select', step_columns: [], step_prompts: ['Would you like our team to send you the details of our current offer?'], step_options: [
        { label: 'Yes, send me the details', goto: '2' },
        { label: 'No thanks', goto: '4' },
      ], step_description: '' },
      { id: '4', step_type: 'message', step_columns: [], step_prompts: ['No problem. Enjoy your visit.'], step_options: [], step_description: '', step_config: { terminal: true } },
      { id: '2', step_type: 'email', step_columns: ['email'], step_prompts: ['Which email should we use for the offer details?'], step_options: [], step_description: '' },
    ],
  },
  {
    id: 'event_registration',
    name: 'Event Registration',
    description: 'Collect an attendee’s details and preferred session for your team to confirm.',
    icon: 'event',
    category: 'booking',
    tags: ['Events', 'Sessions'],
    featured: false,
    appearance: {
      title: 'Request a seat',
      subtitle: 'Choose a session and share your details.',
      home: false,
      summary: true,
      summary_heading: 'Review your registration request',
      summary_confirm_label: 'Request registration',
      success_title: 'Registration request received',
      success_text: 'We have your details and preferred session. Our team will email you to confirm your registration.',
    },
    steps: [
      { id: '1', step_type: 'text', step_columns: ['name'], step_prompts: ['What’s your name?'], step_options: [], step_description: '' },
      { id: '2', step_type: 'email', step_columns: ['email'], step_prompts: ['Which email should we use to confirm your registration?'], step_options: [], step_description: '' },
      { id: '3', step_type: 'single_select', step_columns: ['product_interest'], step_prompts: ['Which session would you prefer?'], step_options: [
        { label: 'Morning session', goto: '' },
        { label: 'Afternoon session', goto: '' },
        { label: 'Evening session', goto: '' },
      ], step_description: '' },
    ],
  },
  ASSISTANT_WELCOME_TEMPLATE,
  HELP_CONTACT_TEMPLATE,
  ...SERVICE_STARTER_TEMPLATES,
].map(template => ({ ...template, appearance: { ...MAGNET_TEMPLATE_DEFAULT_APPEARANCE, ...template.appearance } }));

// ── live catalog ─────────────────────────────────────────────────────────────
// The Template Library org's ENABLED magnets are the real catalog (public
// GET /templates, no auth — see service-api's templateLibrary.js). The bundled
// MAGNET_TEMPLATES above is the OFFLINE FALLBACK and the seed source, nothing
// more: every renderer below reads getTemplates(), which serves the fetched
// list once it lands and the bundle until then (or when the first fetch fails). A
// template added in the app therefore appears here with no client deploy.

let liveTemplates = null;
let loadPromise = null;
let loadedAt = 0;
const CATALOG_TTL_MS = 60 * 1000;

/** DTO → the gallery's template shape (id keeps the client-side name for slug). */
export function fromDTO(t) {
  return {
    id: t.slug, name: t.name, description: t.description, icon: t.icon,
    category: t.category, tags: t.tags || [], featured: !!t.featured,
    ...(t.marketing_excluded === true ? { marketing_excluded: true } : {}),
    appearance: templateAppearance(t.appearance || {}), steps: structuredClone(t.steps || []), appguid: t.appguid,
    ...(Object.hasOwn(t, 'routes') ? { routes: structuredClone(t.routes) } : {}),
  };
}

/** The current catalog: live when fetched, bundled fallback otherwise. */
export function getTemplates() {
  return liveTemplates || MAGNET_TEMPLATES;
}

/**
 * Fetch the live catalog for one minute; concurrent callers share the promise. Always
 * resolves to the best list available; a failed fetch quietly keeps the bundle
 * (the gallery must render on a bad network, not error).
 */
export function loadTemplates() {
  if (liveTemplates && Date.now() - loadedAt < CATALOG_TTL_MS) return Promise.resolve(liveTemplates);
  if (!loadPromise) {
    loadPromise = guardedFetch(`${api.baseUrl}/m5t/v5/templates`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.ok && Array.isArray(d.templates)) {
          liveTemplates = d.templates.map(fromDTO);
          loadedAt = Date.now();
        }
        return getTemplates();
      })
      .catch(() => getTemplates())
      .finally(() => { loadPromise = null; }); // allow refreshes and failed-request retries
  }
  return loadPromise;
}

// Which lead fields a template actually captures, in first-asked order. Derived from
// the steps rather than stored alongside them, so it cannot drift out of sync when a
// template's flow is edited.
export function templateFields(t) {
  const seen = [];
  for (const s of (t.steps || [])) {
    for (const c of (s.step_columns || [])) if (c && !seen.includes(c)) seen.push(c);
  }
  return seen;
}

// ── derived card-face art ────────────────────────────────────────────────────
// The browse page needs a picture of the flow, and a hand-drawn thumbnail per
// template is exactly the drift shape this file already avoids for templateFields():
// edit a template's first question and a stored thumbnail silently lies. So the art is
// GENERATED from steps[0] — the first thing a visitor actually sees — and can never
// describe a flow the template no longer has.

// The faux input shows the answer AFFORDANCE, not the question (the bubble above it
// already carries the question). Keyed by step_type, the same way the SDK picks an
// input's own placeholder.
const INPUT_PLACEHOLDER = {
  text: 'Type your answer',
  email: 'you@example.com',
  phone: '(555) 123-4567',
};

export function derivedPreviewHTML(t) {
  const welcome = t.appearance?.welcome_experience;
  if (welcome?.layout === 'help_hub') {
    return `<div class="mag-tpl-card__preview" aria-hidden="true"><span class="mag-tpl-pv__bubble">${esc(welcome.heading || 'How can we help?')}</span>${(welcome.answers || []).slice(0,2).map(answer => `<span class="mag-tpl-pv__btn">${esc(answer.question)}</span>`).join('')}<span class="mag-tpl-pv__btn">${esc(welcome.primary_label || 'Start a conversation')}</span></div>`;
  }
  if (['concierge','spotlight'].includes(welcome?.layout)) {
    return `<div class="mag-tpl-card__preview" aria-hidden="true"><span class="mag-tpl-pv__bubble">${esc(welcome.heading)}</span><span class="mag-tpl-pv__btn">${esc(welcome.primary_label)}</span></div>`;
  }
  const first = (t.steps || [])[0] || {};
  const line = (first.step_prompts || [])[0] || '';
  let body = '';
  if (first.step_type === 'single_select' || first.step_type === 'multi_select') {
    body = (first.step_options || []).slice(0, 3)
      .map((o) => `<span class="mag-tpl-pv__btn">${esc(o?.label)}</span>`).join('');
  } else if (INPUT_PLACEHOLDER[first.step_type]) {
    body = `<span class="mag-tpl-pv__input">${esc(INPUT_PLACEHOLDER[first.step_type])}</span>`;
  } else if (first.step_type === 'scheduler') {
    body = `<span class="mag-tpl-pv__cal"><span class="material-symbols-outlined" aria-hidden="true">calendar_month</span>Pick a time</span>`;
  }
  // aria-hidden: decorative art. The card's real name/description/buttons carry
  // everything a screen reader needs, so this would only be duplicate noise.
  return `<div class="mag-tpl-card__preview" aria-hidden="true"><span class="mag-tpl-pv__bubble">${esc(line)}</span>${body}</div>`;
}

/**
 * One template card. Shared by the picker modal and the /templates browse page so the
 * two cannot drift — the same reason stepTypes.js and magnetAppearance.js exist.
 * `detail` adds the step count + captured-field line, which the browse page has room
 * for and the modal does not.
 *
 * `gallery` is the browse page's richer face: derived art, tag chips, the featured
 * badge and a secondary Preview button. `canUse` drops the Use button for a persona
 * without provisioning rights (Preview stays — looking is always allowed).
 *
 * Every gallery-only extra is interpolated INLINE, never onto a line of its own, so a
 * plain templateCardHTML(t) still emits byte-for-byte the markup the picker modal has
 * always rendered. Same-elements is not enough here: the modal is the proven surface.
 */
export function templateCardHTML(t, { detail = false, gallery = false, canUse = true } = {}) {
  const fields = detail ? templateFields(t).map(key => {
    const step = (t.steps || []).find(s => (s.step_columns || []).includes(key));
    const label = step?.step_answer?.label || LEAD_CAPTURE_FIELDS.find(field => field.value === key)?.label;
    return label ? label.replace(/ \(.*\)$/, '') : 'Additional answer';
  }) : [];
  const galleryCls = gallery ? ' mag-tpl-card--gallery' : '';
  const catAttr = gallery ? ` data-cat="${esc(t.category || '')}"` : '';
  const badge = gallery && t.featured
    ? `<span class="m5t-badge m5t-badge--featured m5t-featured-corner">Most popular</span>`
    : '';
  const art = gallery ? derivedPreviewHTML(t) : '';
  const tags = gallery && (t.tags || []).length
    ? `<div class="m5t-chip-row mag-tpl-card__tags">${t.tags.map((tag) => `<span class="m5t-chip">${esc(tag)}</span>`).join('')}</div>`
    : '';
  const useBtn = canUse
    ? `<button class="mag-tpl-card__btn" type="button" data-tpl="${t.id}">
        <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>Use this template
      </button>`
    : '';
  const previewBtn = gallery
    ? `<button class="mag-tpl-card__btn mag-tpl-card__btn--ghost" type="button" data-preview="${t.id}">
        <span class="material-symbols-outlined" aria-hidden="true">visibility</span>Preview
      </button>`
    : '';
  return `
    <div class="mag-tpl-card${galleryCls}"${catAttr}>${badge}${art}
      <span class="mag-tpl-card__icon"><span class="material-symbols-outlined" aria-hidden="true">${t.icon}</span></span>
      <span class="mag-tpl-card__name">${t.name}</span>
      <p class="mag-tpl-card__desc">${t.description}</p>${tags}
      ${detail ? `<p class="mag-tpl-card__meta">
        <span><span class="material-symbols-outlined" aria-hidden="true">list_alt</span>${(t.steps || []).length} steps</span>
        ${fields.length ? `<span><span class="material-symbols-outlined" aria-hidden="true">badge</span>Collects ${fields.map(esc).join(', ')}</span>` : ''}
      </p>` : ''}
      ${detail && t.marketing_excluded === true ? '<p class="mag-tpl-card__desc">Requests are saved in Leads, with staff alerts available. CRM sync, webhooks and ad conversions start off.</p>' : ''}
      ${gallery ? `<div class="mag-tpl-card__actions">${useBtn}${previewBtn}</div>` : useBtn}
    </div>`;
}

// ── cross-route handoff ──────────────────────────────────────────────────────
// The /templates browse page cannot provision a magnet itself: that path (owner
// picker → provisionDraft → applyTemplate → open the edit modal) lives in
// magnets-route and depends on its loaded state. So the page parks the choice here
// and navigates to #/magnets, which claims it on init.
//
// The parking mechanism moved to core/routeHandoff.js once the dashboard's campaign
// drill-through became the second thing needing it — see that file for why this is
// module state and not a hash query param, and for the one-shot reasoning. These two
// wrappers stay so callers keep dealing in templates rather than raw handoff keys.
//
// TTL: one-shot alone is not enough here. #/magnets bounces to signin when the guard
// says so, and a click that never reached the destination leaves the choice parked for
// the rest of the session — so a visitor who picks a template, wanders the app for an
// hour, and finally opens Magnets gets a surprise draft they no longer remember asking
// for. Five minutes is "I clicked that just now" and nothing longer.
const PENDING_TEMPLATE_TTL_MS = 5 * 60 * 1000;

export function setPendingTemplate(id) {
  // Retain the selected revision while the catalog refreshes on navigation.
  // templateChanges clones it before the customer edits their independent copy.
  setHandoff(HANDOFF.MAGNET_TEMPLATE, { id, at: Date.now(), template: getTemplates().find(t => t.id === id) || null });
}

/**
 * A parked value → its template, or null when it is stale, unknown, or absent.
 * A bare string is the pre-TTL shape: still honored, since a session can straddle a
 * deploy with the old parker and the new claimant.
 */
function resolveParked(parked) {
  if (!parked) return null;
  if (typeof parked === 'string') return getTemplates().find((x) => x.id === parked) || null;
  const { id, at } = parked;
  if (typeof at === 'number' && (Date.now() - at) >= PENDING_TEMPLATE_TTL_MS) return null;
  if (Object.hasOwn(parked, 'template')) return parked.template;
  return getTemplates().find((x) => x.id === id) || null;
}

export function takePendingTemplate() {
  return resolveParked(takeHandoff(HANDOFF.MAGNET_TEMPLATE));
}

/** Same answer, without spending the one delivery takePendingTemplate() grants. */
export function peekPendingTemplate() {
  return resolveParked(peekHandoff(HANDOFF.MAGNET_TEMPLATE));
}

let stylesInjected = false;
export function ensureTemplateStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-mag-template-styles';
  s.textContent = `
    .mag-tpl-modal .mag-modal { width: min(880px, 92vw); height: auto; max-height: 88vh; }
    .mag-tpl-modal .mag-modal__body { padding: 20px 24px; }
    .mag-tpl-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
    @media (max-width: 640px) { .mag-tpl-grid { grid-template-columns: 1fr; } }
    .mag-tpl-card { border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-lg, 14px); padding: 16px; display: flex; flex-direction: column; gap: 10px; transition: box-shadow .15s ease, border-color .15s ease; }
    .mag-tpl-card:hover { box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08); border-color: var(--color-primary-600, #0072BA); }
    .mag-tpl-card[hidden] { display: none; }
    .mag-tpl-card__icon { width: 42px; height: 42px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: var(--color-accent-soft, #EEF4FF); color: var(--color-accent, #0072BA); }
    .mag-tpl-card__icon .material-symbols-outlined { font-size: 22px; }
    .mag-tpl-card__name { font-weight: 650; font-size: 0.98rem; color: var(--color-text, #0B1220); }
    .mag-tpl-card__desc { font-size: 0.84rem; color: var(--color-text-muted, #4A5A78); line-height: 1.5; flex: 1 1 auto; margin: 0; }
    .mag-tpl-card__btn { align-self: flex-start; appearance: none; border: 1px solid var(--color-accent, #0072BA); background: transparent; color: var(--color-accent, #0072BA); cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 600; padding: 7px 13px; border-radius: 10px; display: inline-flex; align-items: center; gap: 5px; }
    .mag-tpl-card__btn:hover { background: var(--color-accent-soft, #EEF4FF); }
    .mag-tpl-card__btn[disabled] { opacity: 0.55; cursor: default; }
    .mag-tpl-card__meta { display: flex; flex-wrap: wrap; gap: 4px 14px; margin: 0; font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); }
    .mag-tpl-card__meta span { display: inline-flex; align-items: center; gap: 5px; }
    .mag-tpl-card__meta .material-symbols-outlined { font-size: 15px; }
    /* Browse page: room for more than the modal's fixed two columns. */
    .mag-tpl-grid--page { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

    /* ── gallery face (browse page only) ─────────────────────────────────── */
    .mag-tpl-card--gallery { position: relative; overflow: hidden; padding-top: 0; background: var(--color-bg, #fff); }
    .mag-tpl-card__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .mag-tpl-card__btn--ghost { border-color: var(--color-border, #D4DCE8); color: var(--color-text-muted, #4A5A78); }
    .mag-tpl-card__btn--ghost:hover { background: var(--color-surface, #F4F7FB); border-color: var(--color-accent, #0072BA); color: var(--color-accent, #0072BA); }
    .mag-tpl-card__tags { margin-top: -2px; }

    /* ── derived card-face art ───────────────────────────────────────────── */
    .mag-tpl-card__preview { margin: 0 -16px; padding: 16px; min-height: 104px; display: flex; flex-direction: column; align-items: flex-start; gap: 7px; background: linear-gradient(135deg, var(--color-surface, #F4F7FB), var(--color-accent-soft, #D8E6FF)); border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .mag-tpl-pv__bubble { max-width: 100%; font-size: 0.74rem; line-height: 1.35; padding: 7px 11px; border-radius: 12px 12px 12px 3px; background: var(--color-bg, #fff); color: var(--color-text, #0B1220); box-shadow: var(--shadow-sm, 0 1px 3px rgba(11, 18, 32, 0.06)); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mag-tpl-pv__btn, .mag-tpl-pv__input, .mag-tpl-pv__cal { max-width: 100%; font-size: 0.7rem; font-weight: 600; padding: 4px 11px; border-radius: 999px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mag-tpl-pv__btn { background: var(--color-bg, #fff); color: var(--color-accent, #0072BA); border: 1px solid var(--color-accent, #0072BA); }
    .mag-tpl-pv__input { align-self: stretch; background: var(--color-bg, #fff); color: var(--color-text-subtle, #6C7C9A); border: 1px dashed var(--color-border, #D4DCE8); font-weight: 500; }
    .mag-tpl-pv__cal { display: inline-flex; align-items: center; gap: 5px; background: var(--color-bg, #fff); color: var(--color-accent, #0072BA); border: 1px solid var(--color-accent, #0072BA); }
    .mag-tpl-pv__cal .material-symbols-outlined { font-size: 14px; }

    html.ff-no-animations .mag-tpl-card { transition: none; }
    @media (prefers-reduced-motion: reduce) { .mag-tpl-card { transition: none; } }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

/**
 * Open the template gallery modal. Calls onPick(template) when the visitor
 * chooses one — caller is responsible for provisioning + closing the gallery
 * (mirrors openOwnerPickerModal's "resolve, then let the caller act" shape).
 */
export function openTemplateGallery(onPick) {
  loadTemplates(); // fire-and-forget: this render uses what's here, next one is live
  ensureTemplateStyles();
  const scrim = document.createElement('div');
  scrim.className = 'mag-modal-scrim mag-tpl-modal';
  scrim.innerHTML = `
    <div class="mag-modal" role="dialog" aria-modal="true" aria-label="Start from a template">
      <div class="mag-modal__head">
        <span class="mag-modal__title">Start from a template</span>
        <button class="mag-modal__close" type="button" aria-label="Close">&times;</button>
      </div>
      <div class="mag-modal__body">
        <div class="mag-tpl-grid">
          ${getTemplates().map((t) => templateCardHTML(t)).join('')}
        </div>
      </div>
    </div>`;
  document.body.appendChild(scrim);
  requestAnimationFrame(() => { scrim.classList.add('open'); scrim.querySelector('.mag-modal').classList.add('open'); });

  const doClose = () => { scrim.classList.remove('open'); setTimeout(() => scrim.remove(), 180); document.removeEventListener('keydown', onKey); };
  function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); doClose(); } }
  document.addEventListener('keydown', onKey);
  scrim.querySelector('.mag-modal__close').addEventListener('click', doClose);
  scrim.querySelectorAll('[data-tpl]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const template = getTemplates().find((t) => t.id === btn.dataset.tpl);
      if (!template) return;
      scrim.querySelectorAll('[data-tpl]').forEach((b) => { b.disabled = true; });
      doClose();
      onPick(template);
    });
  });
}
